import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button, Divider } from '@treezor/design-system';

import { useCurrentSupportUser } from '@/features/dashboard-users/hooks/useCurrentSupportUser';
import { getMonth } from '@/utils/tools/DateTools';
import { zeroPad } from '@/utils/tools/MathTools';
import { openModal } from '@/store/actions/core.actions';
import { IChangeOwnPasswordModalProps } from '@/features/dashboard-users/components/changePassword/ChangeOwnPasswordModal';
import { useAdvancedRoleDetails } from '@/features/roles/hooks/useAdvancedRole';
import { GeneralInformation } from '@/features/dashboard-users/components/detail/GeneralInformation';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';

export function DashboardUserDetail() {
  const { t } = useTranslation();
  const config = useDashboardConfig();
  const dispatch = useDispatch();

  const {
    isBlocked,
    lastLogin,
    creationDate,
    role: metadataRole,
    secureKey,
    hasSecureKey,
    transports,
    secureKeyCreatedDate,
    ...currentSupportUser
  } = useCurrentSupportUser();

  const role = useAdvancedRoleDetails(metadataRole.slug);

  const onChangePassword = useCallback(() => {
    dispatch(openModal<IChangeOwnPasswordModalProps>('changeOwnPasswordModal', {}));
  }, [dispatch]);

  return (
    <div className="flex h-full">
      <div className="flex flex-col gap-6 p-7 min-w-[400px]">
        <p className="typo-h4-bold text-secondary">{t('pages.administration.supportsUsers.details.account.title')}</p>
        <GeneralInformation
          dashboardUser={currentSupportUser}
          isBlocked={isBlocked}
          lastLogin={lastLogin}
          creationDate={creationDate}
        />
        <Divider thickness="100" direction="horizontal" variant="primary" />

        <div className="flex flex-col gap-4">
          <p className="typo-subtitle-subtitle2 text-tertiary uppercase">
            {t('pages.administration.supportsUsers.details.account.authorization')}
          </p>
          <div className="grid grid-cols-[auto_1fr] gap-y-3 gap-x-9 items-center ">
            <span className="typo-body-regular-paragraph text-secondary">
              {t('pages.administration.supportsUsers.details.account.organisation')}
            </span>
            <span className="typo-body-regular-paragraph text-secondary">{config?.site?.name}</span>
            {role && (
              <>
                <span className="typo-body-regular-paragraph text-secondary">
                  {t('pages.administration.supportsUsers.details.account.role')}
                </span>
                <span className="typo-body-regular-paragraph text-secondary">
                  {t(`roles.advancedRoles.names.${role.slug}`)}
                </span>
              </>
            )}
          </div>
        </div>

        <Divider thickness="100" direction="horizontal" variant="primary" />

        <div className="flex flex-col gap-4">
          <p className="typo-subtitle-subtitle2 text-tertiary uppercase">
            {t('pages.administration.supportsUsers.details.account.security')}
          </p>
          <div className="grid grid-cols-[auto_1fr] gap-y-3 gap-x-9 items-center ">
            <span className="typo-body-regular-paragraph text-secondary">
              {t('pages.administration.supportsUsers.details.account.2FA')}
            </span>
            <span className={`typo-body-regular-paragraph ${hasSecureKey ? 'text-success' : 'text-error'}`}>
              {hasSecureKey
                ? t('pages.administration.supportsUsers.table.status.2FA.value.enabled')
                : t('pages.administration.supportsUsers.table.status.2FA.value.disabled')}
            </span>
          </div>
        </div>
      </div>

      <Divider thickness="100" direction="vertical" variant="primary" length="inherit" />

      <div className="flex flex-col p-7 gap-7 w-full">
        <p className="typo-h5-bold text-secondary">
          {t('pages.administration.supportsUsers.details.loginAndSecurity.title')}
        </p>

        <div className="flex flex-col gap-5">
          <p className="typo-h6-bold text-secondary">
            {t('pages.administration.supportsUsers.details.loginAndSecurity.login')}
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex">
              <span className="typo-subtitle-subtitle1">
                {t('pages.administration.supportsUsers.details.loginAndSecurity.password')}
              </span>
              <Button variant="ghost" size="s" className="ml-auto" onClick={onChangePassword}>
                {t('pages.cores.update')}
              </Button>
            </div>

            <Divider thickness="100" direction="horizontal" variant="primary" />

            <div className="flex flex-col gap-3">
              <p className="typo-h6-bold">{t('pages.administration.supportsUsers.details.loginAndSecurity.2FA')}</p>
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  <span className="typo-body-regular-small text-secondary ">
                    {t('pages.administration.supportsUsers.details.loginAndSecurity.tag')}
                  </span>
                  <span className="typo-body-medium-small text-secondary ">{secureKey?.secureKeyTag ?? '/'}</span>
                </div>
                <div className="flex gap-3">
                  <span className="typo-body-regular-small text-secondary ">
                    {t('pages.administration.supportsUsers.details.loginAndSecurity.transports')}
                  </span>
                  <span className="typo-body-medium-small text-secondary">{transports ?? '/'}</span>
                </div>
                <div className="flex gap-3">
                  <span className="typo-body-regular-small text-secondary ">
                    {t('pages.administration.supportsUsers.details.loginAndSecurity.createdOn')}
                  </span>
                  <span className="typo-body-medium-small text-secondary">
                    {secureKeyCreatedDate
                      ? `${t(getMonth(secureKeyCreatedDate.getMonth())).slice(0, 3)} ${zeroPad(secureKeyCreatedDate.getDate(), 2)}, ${secureKeyCreatedDate.getFullYear()}`
                      : '/'}
                  </span>
                </div>
              </div>
            </div>
            <Divider thickness="100" direction="horizontal" variant="primary" />
          </div>
        </div>
      </div>
    </div>
  );
}
